import useSWR from 'swr';
import { api } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';
import { realtimeConfig } from '@/lib/swr-config';
import { getErrorMessage, isConcurrentBookingError } from '@/lib/error-handling';
import {
  invalidateAllBookingRelatedCaches,
  invalidateBookingRequestsCaches,
} from '@/lib/cache-utils';
import type {
  BookingRequest,
  BookingRequestStatus,
  AcceptBookingData,
  DeclineBookingData,
} from '@/types/booking';

// ============================================================================
// Types
// ============================================================================

interface BookingRequestsResponse {
  requests: BookingRequest[];
}

interface UseBookingRequestsOptions {
  status?: BookingRequestStatus | 'ALL';
  enabled?: boolean;
}

interface UseBookingRequestsReturn {
  // Data
  requests: BookingRequest[];
  filteredRequests: BookingRequest[];

  // Counts by status
  pendingCount: number;
  acceptedCount: number;
  declinedCount: number;
  totalCount: number;

  // State
  isLoading: boolean;
  error: Error | null;

  // Actions
  acceptRequest: (id: string, data?: AcceptBookingData) => Promise<BookingRequest | null>;
  declineRequest: (id: string, data: DeclineBookingData) => Promise<BookingRequest | null>;

  // Utilities
  refetch: () => Promise<BookingRequestsResponse | undefined>;
  mutateRequests: (
    data?: BookingRequest[] | ((current: BookingRequest[] | undefined) => BookingRequest[])
  ) => Promise<BookingRequest[] | undefined>;
}

// ============================================================================
// SWR Key Functions
// ============================================================================

const BOOKING_REQUESTS_KEY = '/api/caregivers/me/booking-requests';

function getBookingRequestsKey(options?: { status?: BookingRequestStatus | 'ALL' }) {
  if (options?.status && options.status !== 'ALL') {
    return `${BOOKING_REQUESTS_KEY}?status=${options.status}`;
  }
  return BOOKING_REQUESTS_KEY;
}

// ============================================================================
// API Functions
// ============================================================================

async function fetchBookingRequests(key: string): Promise<BookingRequest[]> {
  try {
    const response = await api.get<BookingRequestsResponse>(key);
    return response.requests || [];
  } catch (error) {
    console.error('Failed to fetch booking requests:', error);
    throw error;
  }
}

async function acceptBookingRequest(id: string, data?: AcceptBookingData): Promise<BookingRequest> {
  const response = await api.put<{ request: BookingRequest }>(
    `/api/caregivers/me/booking-requests/${id}/accept`,
    data || {}
  );
  return response.request;
}

async function declineBookingRequest(
  id: string,
  data: DeclineBookingData
): Promise<BookingRequest> {
  const response = await api.put<{ request: BookingRequest }>(
    `/api/caregivers/me/booking-requests/${id}/decline`,
    data
  );
  return response.request;
}

// ============================================================================
// Utility Functions
// ============================================================================

function sortRequestsByDate(requests: BookingRequest[]): BookingRequest[] {
  return [...requests].sort((a, b) => {
    // Sort by creation date, newest first
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  });
}

function filterRequestsByStatus(
  requests: BookingRequest[],
  status?: BookingRequestStatus | 'ALL'
): BookingRequest[] {
  if (!status || status === 'ALL') {
    return requests;
  }
  return requests.filter(request => request.status === status);
}

function getRequestCounts(requests: BookingRequest[]) {
  const counts = {
    pendingCount: 0,
    acceptedCount: 0,
    declinedCount: 0,
    totalCount: requests.length,
  };

  requests.forEach(request => {
    switch (request.status) {
      case 'REQUESTED':
        counts.pendingCount++;
        break;
      case 'ACCEPTED':
        counts.acceptedCount++;
        break;
      case 'DECLINED':
        counts.declinedCount++;
        break;
    }
  });

  return counts;
}

// ============================================================================
// Main Hook
// ============================================================================

export function useBookingRequests(
  options: UseBookingRequestsOptions = {}
): UseBookingRequestsReturn {
  const { status, enabled = true } = options;
  const { success: showSuccess, error: showError } = useToast();

  // Generate SWR key
  const swrKey = enabled ? getBookingRequestsKey({ status }) : null;

  // Fetch data with SWR using realtime configuration
  const {
    data: requests = [],
    error,
    isLoading,
    mutate: mutateSWR,
  } = useSWR<BookingRequest[], Error>(swrKey, fetchBookingRequests, realtimeConfig);

  // Sort requests by creation date (newest first)
  const sortedRequests = sortRequestsByDate(requests);

  // Filter requests by status if specified
  const filteredRequests = filterRequestsByStatus(sortedRequests, status);

  // Calculate counts
  const { pendingCount, acceptedCount, declinedCount, totalCount } =
    getRequestCounts(sortedRequests);

  // ============================================================================
  // Optimistic Update Helpers
  // ============================================================================

  const mutateRequests = async (
    data?: BookingRequest[] | ((current: BookingRequest[] | undefined) => BookingRequest[])
  ) => {
    return mutateSWR(data, { revalidate: false });
  };

  const refetch = async () => {
    const result = await mutateSWR();
    return result ? { requests: result } : undefined;
  };

  // ============================================================================
  // Actions with Optimistic Updates
  // ============================================================================

  const acceptRequest = async (
    id: string,
    data?: AcceptBookingData
  ): Promise<BookingRequest | null> => {
    try {
      // Find the original request for rollback
      const originalRequest = requests.find(req => req.id === id);
      if (!originalRequest) {
        throw new Error('Booking request not found');
      }

      // Create optimistic update
      const optimisticRequest: BookingRequest = {
        ...originalRequest,
        status: 'ACCEPTED',
        bookingId: `temp-booking-${Date.now()}`,
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update the cache
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? optimisticRequest : req))
      );

      // Make the API call
      const updatedRequest = await acceptBookingRequest(id, data);

      // Update cache with real data from server
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? updatedRequest : req))
      );

      // Refresh related caches (booking requests, bookings, calendar, availability).
      try {
        await invalidateAllBookingRelatedCaches();
      } catch (cacheError) {
        console.warn('Failed to refresh caches after accepting booking request:', cacheError);
      }

      showSuccess('Booking request accepted', {
        description: `You have accepted the booking request from ${originalRequest.careClientName}.`,
      });

      return updatedRequest;
    } catch (error) {
      console.error('Failed to accept booking request:', error);

      // Revert optimistic update on error
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? requests.find(r => r.id === id) || req : req))
      );

      // Get user-friendly error message
      const errorMessage = getErrorMessage(error);
      const isConcurrentError = isConcurrentBookingError(error);

      showError(isConcurrentError ? 'Time Slot Unavailable' : 'Error accepting request', {
        description: errorMessage,
      });
      return null;
    }
  };

  const declineRequest = async (
    id: string,
    data: DeclineBookingData
  ): Promise<BookingRequest | null> => {
    try {
      // Find the original request for rollback
      const originalRequest = requests.find(req => req.id === id);
      if (!originalRequest) {
        throw new Error('Booking request not found');
      }

      // Create optimistic update
      const optimisticRequest: BookingRequest = {
        ...originalRequest,
        status: 'DECLINED',
        updatedAt: new Date().toISOString(),
      };

      // Optimistically update the cache
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? optimisticRequest : req))
      );

      // Make the API call
      const updatedRequest = await declineBookingRequest(id, data);

      // Update cache with real data from server
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? updatedRequest : req))
      );

      // Refresh booking request caches. Non-fatal if refresh fails.
      try {
        await invalidateBookingRequestsCaches();
      } catch (cacheError) {
        console.warn('Failed to refresh booking request caches after decline:', cacheError);
      }

      showSuccess('Booking request declined', {
        description: `You have declined the booking request from ${originalRequest.careClientName}.`,
      });

      return updatedRequest;
    } catch (error) {
      console.error('Failed to decline booking request:', error);

      // Revert optimistic update on error
      await mutateRequests(current =>
        (current || []).map(req => (req.id === id ? requests.find(r => r.id === id) || req : req))
      );

      // Get user-friendly error message
      const errorMessage = getErrorMessage(error);

      showError('Error declining request', { description: errorMessage });
      return null;
    }
  };

  return {
    // Data
    requests: sortedRequests,
    filteredRequests,

    // Counts
    pendingCount,
    acceptedCount,
    declinedCount,
    totalCount,

    // State
    isLoading,
    error: error || null,

    // Actions
    acceptRequest,
    declineRequest,

    // Utilities
    refetch,
    mutateRequests,
  };
}

// ============================================================================
// Convenience Hooks
// ============================================================================

/**
 * Hook for fetching pending booking requests only
 */
export function usePendingBookingRequests() {
  return useBookingRequests({ status: 'REQUESTED' });
}

/**
 * Hook for fetching accepted booking requests only
 */
export function useAcceptedBookingRequests() {
  return useBookingRequests({ status: 'ACCEPTED' });
}

/**
 * Hook for fetching declined booking requests only
 */
export function useDeclinedBookingRequests() {
  return useBookingRequests({ status: 'DECLINED' });
}

/**
 * Hook for fetching all booking requests
 */
export function useAllBookingRequests() {
  return useBookingRequests({ status: 'ALL' });
}
